import {computed, defineComponent, onBeforeMount, ref} from 'vue';
import {storeToRefs} from "pinia";
import {useRoute} from "vue-router";
import {useInvoicingStore} from "@/app/stores/invoicing-store.ts";
import {
    AllInvoicesQuery,
    AthumiService,
    DocumentType,
    type Invoice,
    PaymentMethod,
} from "@/api/enterprise-gateway-api.proxy.ts";
import {useFormatting} from "@/composables/currency-format.ts";
import {PagedResult} from "@/app/shared/helpers/paged-result.ts";
import {useUserStore} from "@/app/stores/user-store.ts";
import {useIsMobile} from "@/app/shared/composables/useIsMobile.ts";
import {Button, Column, DataTable, Panel} from 'primevue';
import ProgressSpinner from 'primevue/progressspinner';
import AegInvoicesTable from '@/app/shared/components/aeg-invoices-table/aeg-invoices-table.vue';
import AegPaginator from '@/app/shared/components/aeg-paginator/aeg-paginator.vue';
import AegInvoiceCards from '@/app/shared/components/aeg-invoice-cards/aeg-invoice-cards.vue';

export default defineComponent({
    name: 'AegInvoices',
    components: {
        AegInvoiceCards,
        AegPaginator, AegInvoicesTable, Panel, Button, DataTable, Column, ProgressSpinner },
    props: {},
    setup() {
        const route = useRoute();
        const invoiceStore = useInvoicingStore();
        const userStore = useUserStore();
        const {formatCurrency, formatDate} = useFormatting();

        const {isLoading, errorLoading} = storeToRefs(invoiceStore);
        const invoices = ref<PagedResult<Invoice>>(PagedResult.createEmpty<Invoice>());
        const emptyState = ref<boolean>(false);
        const isMobile = useIsMobile();
        const downloadFileUriLoading = ref<boolean>(false);
        const downloadFileUri = ref<string | undefined>('');

        const searchQuery = ref<AllInvoicesQuery>(AllInvoicesQuery.fromJS({
            actorId: userStore.user.actorInfo?.id,
            pageSize: 50,
            page: 1,
            service: route.params.service as AthumiService,
        }));
        const defaultSearchQuery = ref<AllInvoicesQuery>();
        const changePage = async (pageNumber: number) => {
            await goToPage(pageNumber);
        }

        const onSearch = async (searchCriteria: AllInvoicesQuery) => {
            searchCriteria.page = searchQuery.value.page;
            searchCriteria.pageSize = searchQuery.value.pageSize;
            searchCriteria.actorId = userStore.user.actorInfo?.id;
            searchCriteria.service = route.params.service as AthumiService;
            searchQuery.value = searchCriteria;
            await loadInvoices();
        };

        const goToPage = async (page: number) => {
            invoices.value.pagingInfo.page = page;
            searchQuery.value.page = page;
            const result = await invoiceStore.loadInvoicesForUser(searchQuery.value);
            if (!result) {
                return;
            }
            invoices.value = result.pagedResult;
            downloadFileUri.value = result.downloadUrl;
            emptyState.value = result.pagedResult.items.length === 0 && !errorLoading.value && !isLoading.value;
        }

        const error = computed(() => {
            return errorLoading.value || userStore.user.actorServiceDown;
        });

        const getDocumentTypeLabel = (documentType?: DocumentType): string => {
            return invoiceStore.getDocumentTypeLabel(documentType);
        };

        const isCreditsType = (invoice: Invoice): boolean => {
            return invoice.paymentMethod === PaymentMethod.Credits;
        }

        const loadInvoices = async () => {
            await goToPage(1);
        };

        const getInvoiceTypeIcon = (documentType?: DocumentType, paymentMethod?: PaymentMethod): string => {
            return invoiceStore.getInvoiceTypeIcon(documentType, paymentMethod);
        };

        const isTopUpType = (documentType?: DocumentType): boolean => {
            return invoiceStore.isTopUpType(documentType);
        };

        onBeforeMount(async () => {
            await loadInvoices();
        });

        return {
            isLoading,
            error,
            emptyState,
            invoices,
            isMobile,
            downloadFileUriLoading,
            downloadFileUri,
            defaultSearchQuery,
            isCreditsType,
            getDocumentTypeLabel,
            getInvoiceTypeIcon,
            isTopUpType,
            formatDate,
            formatCurrency,
            loadInvoices,
            changePage,
            onSearch
        }
    }
})
